import { HttpClient, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

/**
 * Interceptor SOLO de MEDIVELIUS, copia del de evolti y mio3 (comparten backend). Adapta la
 * edicion de roles de la libreria compartida (<lib-users>) al contrato del
 * backend, sin tocar libs/ ni afectar a las demas apps.
 *
 * La lib guarda el rol con `PUT /api/users/{id}/` mandando el rol por NOMBRE
 * (ej. role: ["SuperAdmin"]). El backend NO acepta eso: guardaba el resto de
 * campos y fallaba con el rol, asi que el modal no se cerraba y el rol no
 * cambiaba. Exige `POST /api/users/{id}/assign-roles/` con `role_ids: number[]`.
 *
 * Aqui interceptamos ese PUT cuando trae rol y lo traducimos:
 *   1) PUT /api/users/{id}/  con los demas campos (email, nombre, estado...) SIN el rol.
 *   2) POST /api/users/{id}/assign-roles/  con los role_ids resueltos desde el
 *      catalogo /api/roles/ (mapeo nombre -> id).
 * Para la lib es transparente: recibe una respuesta OK como si fuera un PUT normal.
 */

interface RoleCatalogItem {
  id: number;
  name: string;
}

// Catálogo de roles cacheado por URL para no pedir /api/roles/ en cada guardado.
const rolesCatalogCache = new Map<string, Observable<RoleCatalogItem[]>>();

function normalizeRolesCatalog(res: unknown): RoleCatalogItem[] {
  const arr = Array.isArray(res)
    ? res
    : ((res as { data?: unknown[] })?.data ?? (res as { results?: unknown[] })?.results ?? []);

  return (arr as Array<Record<string, unknown>>)
    .map((r) => ({ id: Number(r?.['id']), name: String(r?.['name'] ?? '').trim() }))
    .filter((r) => Number.isFinite(r.id) && r.name.length > 0);
}

function loadRolesCatalog(http: HttpClient, rolesUrl: string): Observable<RoleCatalogItem[]> {
  let cached = rolesCatalogCache.get(rolesUrl);
  if (!cached) {
    cached = http.get<unknown>(rolesUrl).pipe(map(normalizeRolesCatalog), shareReplay(1));
    rolesCatalogCache.set(rolesUrl, cached);
  }
  return cached;
}

export const usersAssignRolesInterceptor: HttpInterceptorFn = (req, next) => {
  const userUpdateMatch = req.url.match(/^(.*)\/api\/users\/(\d+)\/?$/);
  const body = req.body as { role?: unknown } | null;

  // Cualquier otra petición (incluido el PUT sin rol: toggle estado, bloquear…) pasa intacta.
  if (req.method !== 'PUT' || !userUpdateMatch || !body || !('role' in body) || body.role == null) {
    return next(req);
  }

  const http = inject(HttpClient);
  const [, baseUrl, userId] = userUpdateMatch;
  const rolesUrl = `${baseUrl}/api/roles/`;
  const assignUrl = `${baseUrl}/api/users/${userId}/assign-roles/`;

  const roleNames = (Array.isArray(body.role) ? (body.role as unknown[]) : [body.role])
    .map((r) => String(r ?? '').trim())
    .filter(Boolean);

  // Body del PUT sin el campo role (eso si lo acepta el backend).
  const bodyWithoutRole: Record<string, unknown> = { ...(body as Record<string, unknown>) };
  delete bodyWithoutRole['role'];

  return loadRolesCatalog(http, rolesUrl).pipe(
    switchMap((catalog) => {
      const idByName = new Map(catalog.map((r) => [r.name.toLowerCase(), r.id]));
      const roleIds = roleNames
        .map((n) => idByName.get(n.toLowerCase()))
        .filter((id): id is number => id != null);

      // 1) Actualiza los demás campos del usuario (sin rol).
      return http.put<unknown>(req.url, bodyWithoutRole).pipe(
        // 2) Asigna los roles por ID con el endpoint propio del backend.
        switchMap((updated) =>
          roleIds.length
            ? http.post(assignUrl, { role_ids: roleIds }).pipe(map(() => updated))
            : of(updated)
        ),
        map((updated) => new HttpResponse({ status: 200, body: updated, url: req.url }))
      );
    })
  );
};
